/**
 * Polyfill for the commandfor/command attribute spec (Invoker Commands).
 *
 * Only activates when the browser lacks native support. Replicates the
 * spec behavior: clicking a button with [commandfor] dispatches a
 * CommandEvent on the target element. For standard commands, the default
 * action executes if the event isn't prevented. For custom commands
 * (--prefixed), only the event is dispatched -- the target element is
 * responsible for handling it.
 *
 * Standard commands:
 *   show-modal      -> target.showModal()       (dialog)
 *   close           -> target.close()            (dialog)
 *   request-close   -> target.requestClose()     (dialog, cancelable)
 *   show-popover    -> target.showPopover()      (popover)
 *   hide-popover    -> target.hidePopover()      (popover)
 *   toggle-popover  -> target.togglePopover()    (popover)
 *
 * Custom commands (--prefixed) are dispatched as events but have no
 * built-in default action. The target element handles them via
 * addEventListener('command', ...).
 *
 * When native commandfor is available, this file does nothing.
 *
 * Spec: https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/command
 */

if (!('commandForElement' in HTMLButtonElement.prototype)) {
    // console.error('Polyfills command for <commandForElement>');

    const ACTIONS = {
        'show-modal': (el) => el.showModal(),
        close: (el) => el.close(),
        'show-popover': (el) => el.showPopover(),
        'hide-popover': (el) => el.hidePopover(),
        'toggle-popover': (el) => el.togglePopover(),
    };

    document.addEventListener('click', (e) => {
        const btn = e.target.closest('[commandfor]');
        if (!btn) return;

        // console.log(`[polyfill] ${btn}`);

        const targetId = btn.getAttribute('commandfor');
        const command = btn.getAttribute('command');
        const target = document.getElementById(targetId);
        if (!target || !command) return;

        // Dispatch command event on the target, matching native behavior.
        // The event is cancelable so listeners can preventDefault() to
        // block the default action for standard commands.
        const event = new CustomEvent('command', {
            bubbles: true,
            cancelable: true,
            detail: { command, invoker: btn },
        });
        const allowed = target.dispatchEvent(event);

        // console.log('[commandfor]', target, event, allowed);
        // Standard commands execute their default action if not prevented.
        // Custom commands (--prefixed) have no default action -- the
        // target handles them entirely via the event listener.
        if (allowed && ACTIONS[command]) {
            // console.log('[commandfor standard command]', target, event, allowed);
            ACTIONS[command](target);
        }

        // request-close is special: it's cancelable at the dialog level.
        // If the dialog doesn't prevent it, close proceeds.
        if (allowed && command === 'request-close' && typeof target.requestClose === 'function') {
            target.requestClose();
        }
    });
}
